/**
 * Client-side accept gate for card (deliverable) + comment attachments —
 * shared by the Notes-tab picker, the description-field OS-file drop zone,
 * and the comment composer so every surface enforces the same rules.
 *
 * Mirrors the BE (`validateMimeForKind` for deliverable + comment kinds):
 * ANY file type is accepted. A card / comment is the team's own workspace
 * — they attach docs, spreadsheets, zips, etc. and know what they're
 * uploading (same rationale + shape as the `wiki-file` kind). Size is the
 * only client gate; the BE re-validates.
 *
 * THE SIZE CEILING IS PER-MIME, NOT ONE NUMBER. A playable video gets
 * 200 MiB and everything else gets 100 MB, because the same clip has to
 * be postable on a card, in a comment, in chat and on the wiki — the wiki
 * already took 200 MiB and refusing it here made the limit a property of
 * where you dropped the file rather than of the file. Read the cap through
 * `maxBytesForAttachment(mime)`; the bare `MAX_ATTACHMENT_BYTES` is only
 * the non-video half and gating on it alone re-creates the split.
 *
 * Browsers hide a dragged file's MIME during the drag, so validation runs
 * AFTER the drop (or file-picker change).
 *
 * One consumer sits outside the name: the wiki `/file` slash modal
 * (`FileUploadFlow`) presigns `wiki-file`, not a card kind, and reads
 * `maxBytesForAttachment` because the BE gives that kind the identical
 * pair of ceilings. The numbers coinciding is what makes the reuse
 * correct, so a card-only change to `MAX_ATTACHMENT_BYTES` would move the
 * wiki gate too, silently and with no test failing. Change one, check the
 * BE's `wiki-file` branch — or give that surface its own constants, the
 * way `use-wiki-doc-file-drop` already does.
 */

import { VIDEO_ATTACHMENT_MAX_BYTES, isVideoEmbedMime } from './video-embed-mime'

/** 100 MB — the ceiling for everything that is not a playable video. */
export const MAX_ATTACHMENT_BYTES = 100 * 1024 * 1024

/**
 * Which ceiling applies to this file. Mirrors the BE's per-MIME branch in
 * `validateMimeForKind` for the deliverable + comment kinds.
 */
export function maxBytesForAttachment(mime: string): number {
  return isVideoEmbedMime(mime) ? VIDEO_ATTACHMENT_MAX_BYTES : MAX_ATTACHMENT_BYTES
}

/**
 * Which ceiling the "too large" copy should name. The catalog strings are
 * an ICU `select`, so the branch has to be resolved here rather than by
 * interpolating a number — a bare figure in a sentence is exactly the
 * embedded format spec the i18n rules keep out of catalog values.
 */
export type AttachmentSizeKind = 'video' | 'other'

export function sizeKindFor(mime: string): AttachmentSizeKind {
  return isVideoEmbedMime(mime) ? 'video' : 'other'
}

/**
 * Any non-empty content type is accepted. Kept as a function (not an
 * inline check) so a future per-type restriction has a single home. The
 * drop / picker paths default a typeless file to `application/octet-stream`
 * before calling this, so this is effectively "accept everything".
 */
export function isAllowedAttachmentMime(mime: string): boolean {
  return mime.trim().length > 0
}
